import { ColumnDef } from "@tanstack/react-table";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { IoSearch } from "react-icons/io5";
import { LuTrash2, LuX } from "react-icons/lu";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "usehooks-ts";

import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { Avatar } from "../../../components/newUI/avatar";
import { Button } from "../../../components/newUI/button";
import { ListPager } from "../../../components/pagination/ListPager";
import { Table } from "../../../components/table/Table";
import { getInitialsFromFullName } from "../../../constants/constants";
import { CustomerModel } from "../../../factories/customers/models/customerModel";
import { useListMemory, useRememberList } from "../../../hooks/useListMemory";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useDeleteContactMutation,
  useGetArchivedCustomersQuery,
} from "../../../slices/ContactsSlice";
import { FormSection } from "../../../support/FormSection";
import { QBO_LOGO_URL } from "../../../utils/qbo";
import {
  ClientEmailLink,
  useClientEmailCompose,
} from "../clientEmail/useClientEmailCompose";
import { ContactDeleteConfirmModal } from "../components/ContactDeleteConfirmModal";
import TooltipDeactivatedMessage from "../components/TooltipDeactivatedMessage";
import {
  contactsCountLabel,
  listDisplayName,
} from "../utils/contactsList.utils";
import { restoreContactsList } from "../utils/contactsListMemory";
import { navigateToContact } from "../utils/navigateToContacts";

type Params = {
  page: string;
};

export const ArchivedContacts: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const linkedFirm = getLinkedFirm();
  const orgId = linkedFirm?.orgId || "";
  const userId = useAppSelector((state) => state.appReducer.user?.id);

  const pageRaw = Number(searchParams.get("page") || 1);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  const [selectedCustomers, setSelectedCustomers] = useState<CustomerModel[]>(
    [],
  );
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const { openCompose, composeModal } = useClientEmailCompose();
  // The search survives opening a client and coming Back (Roadmap 1851).
  const memory = useListMemory<{ search: string }>({ scope: orgId });
  const [search, setSearch] = useState(
    () => restoreContactsList(memory.restored).search,
  );

  const debounceSearch = useDebounce(search, 200);
  const canLoad = Boolean(orgId && userId);

  const {
    data: contacts,
    isLoading: isLoadingContacts,
    isError: isLoadError,
    refetch,
  } = useGetArchivedCustomersQuery(
    {
      orgId,
      isActive: false,
      userId: userId || "",
      tagIds: [],
      yearEndMonth: [],
      // The API refuses the request without this list ("OrganizationTypeIds
      // field is required"); empty = all types, like the Active tab.
      organizationTypeIds: [],
      customerName: null,
      sortOrder: "asc",
      // Same default sort as the Active tab (the API passes it on as is).
      sortColumn: "Name",
      searchQuery: debounceSearch || "",
      pageSize: 20,
      pageNumber: page,
    },
    {
      skip: !canLoad,
    },
  );

  const [deleteContact, { isLoading: isDeleting }] = useDeleteContactMutation();

  const remembered = useMemo(() => ({ search }), [search]);
  useRememberList(memory, remembered, !isLoadingContacts && !!contacts);

  useEffect(() => {
    if (!contacts?.length) {
      setSelectedCustomers([]);
      return;
    }

    setSelectedCustomers((prev) =>
      prev.filter((selected) =>
        contacts.some((contact) => contact.customerId === selected.customerId),
      ),
    );
  }, [contacts]);

  const allSelected = useMemo(() => {
    if (!contacts?.length) return false;

    return contacts.every((contact) =>
      selectedCustomers.some(
        (selected) => selected.customerId === contact.customerId,
      ),
    );
  }, [contacts, selectedCustomers]);

  const setPageParam = useCallback(
    (nextPage: number) => {
      const params: Params = {
        page: String(nextPage || 1),
      };

      setSearchParams(params);
    },
    [setSearchParams],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!contacts?.length) {
        setSelectedCustomers([]);
        return;
      }

      setSelectedCustomers(checked ? contacts : []);
    },
    [contacts],
  );

  const handleToggleCustomer = useCallback(
    (customer: CustomerModel, checked: boolean) => {
      if (!checked) {
        setSelectedCustomers((prev) =>
          prev.filter((item) => item.customerId !== customer.customerId),
        );
        return;
      }

      setSelectedCustomers((prev) => {
        if (prev.some((item) => item.customerId === customer.customerId)) {
          return prev;
        }

        return [...prev, customer];
      });
    },
    [],
  );

  const handleDeleteContacts = useCallback(() => {
    if (!selectedCustomers.length || !orgId) return;

    setIsOpenDelete(false);

    deleteContact({
      body: selectedCustomers.map((item) => item.customerId),
      orgId,
    });

    setSelectedCustomers([]);
  }, [deleteContact, orgId, selectedCustomers]);

  const selectedContactName =
    selectedCustomers.length === 1 ? selectedCustomers[0].customerName : "";
  const deleteModalTitle =
    selectedCustomers.length > 1 ? "Delete selected Contacts?" : undefined;
  const deleteModalDescription =
    selectedCustomers.length > 1
      ? "Do you want to permanently delete these contacts?<br />All associated work items, notes, files, payments, and invoices will also be deleted. This action cannot be undone."
      : undefined;

  const hasSelectedCustomers = selectedCustomers.length > 0;
  const selectedCustomersLabel = `${selectedCustomers.length} selected`;

  const contactColumns = useMemo<ColumnDef<CustomerModel, unknown>[]>(
    () => [
      {
        id: "select",
        enableSorting: false,
        header: () => (
          <input
            className={
              "checkbox scale-75 border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
            checked={allSelected}
            type={"checkbox"}
          />
        ),
        cell: ({ row }) => (
          <input
            className={
              "checkbox scale-75 border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
            }
            onChange={(e) =>
              handleToggleCustomer(row.original, e.target.checked)
            }
            type={"checkbox"}
            checked={selectedCustomers.some(
              (selected) => selected.customerId === row.original.customerId,
            )}
          />
        ),
        meta: {
          headerClassName:
            "px-4 pt-2 text-start text-sm normal-case text-gray-400",
          className: "w-[30px] pl-4",
        },
      },
      {
        id: "contact",
        enableSorting: false,
        header: "Contact",
        cell: ({ row }) => {
          const item = row.original;

          return (
            <div className={"flex min-w-0 items-center gap-3 text-sm"}>
              <div className={"shrink-0"}>
                <Avatar
                  id={item.customerId || item.id || item.customerName}
                  color={item.businessContactId ? "secondary" : "primary"}
                  size={"sm"}
                  label={getInitialsFromFullName(item.customerName) || ""}
                  fullName={item.customerName}
                />
              </div>

              <div
                onClick={() =>
                  navigate(
                    navigateToContact({
                      type: item.businessContactId ? "organization" : "contact",
                      id: item.businessContactId || item.contactId || "",
                      customerId: item.customerId || "",
                    }),
                  )
                }
                className={
                  "link-primary flex max-w-[200px] cursor-pointer gap-1 text-sm font-semibold hover:underline"
                }>
                <span className={"min-w-0 truncate"}>{item.customerName}</span>
                {item.isSyncedWithQBO && (
                  <img
                    src={QBO_LOGO_URL}
                    height={"18px"}
                    width={"18px"}
                    alt={"QBO synced"}
                    className={"shrink-0"}
                  />
                )}
              </div>

              <TooltipDeactivatedMessage />
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm normal-case text-gray-400",
          className: "max-w-[200px] px-4 py-2",
        },
      },
      {
        id: "displayName",
        enableSorting: false,
        header: "Display name",
        cell: ({ row }) => (
          <div className={"flex items-center gap-3 text-sm"}>
            <div
              className={
                "max-w-[200px] truncate text-sm font-semibold text-gray-800"
              }>
              {listDisplayName(row.original)}
            </div>
          </div>
        ),
        meta: {
          headerClassName: "p-4 text-start text-sm normal-case text-gray-400",
          className: "max-w-[200px] px-4 py-2",
        },
      },
      {
        id: "phoneNumber",
        enableSorting: false,
        header: "Phone Number",
        cell: ({ row }) => (
          <div className={"text-sm font-semibold normal-case text-gray-800"}>
            {row.original.phoneNumber?.split(",")[0]}
          </div>
        ),
        meta: {
          headerClassName: "p-4 text-start text-sm normal-case text-gray-400",
          className: "px-4 py-2",
        },
      },
      {
        id: "email",
        enableSorting: false,
        header: "Email Address",
        cell: ({ row }) => {
          const email = row.original.email?.split(",")[0];
          return (
            <div
              className={
                "link-primary max-w-[200px] truncate text-sm font-semibold"
              }>
              <ClientEmailLink
                email={email}
                className={"max-w-full truncate text-left hover:underline"}
                onOpen={() =>
                  openCompose({
                    email,
                    customerId: row.original.customerId,
                    name: row.original.customerName,
                    businessContactId: row.original.businessContactId,
                  })
                }
              />
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm normal-case text-gray-400",
          className: "max-w-[200px] px-4 py-2",
        },
      },
      {
        id: "contactType",
        enableSorting: false,
        header: "Contact type",
        cell: ({ row }) => (
          <div className={"text-sm font-semibold normal-case text-gray-800"}>
            {row.original.customerType}
          </div>
        ),
        meta: {
          headerClassName: "p-4 text-start text-sm normal-case text-gray-400",
          className: "px-4 py-2",
        },
      },
      {
        id: "allWork",
        enableSorting: false,
        header: "All work",
        cell: ({ row }) => (
          <div className={"text-sm font-semibold normal-case text-gray-800"}>
            {row.original.allWorkCounts}
          </div>
        ),
        meta: {
          headerClassName: "p-4 text-start text-sm normal-case text-gray-400",
          className: "px-4 py-2",
        },
      },
      {
        // QA batch 15 (CONT-023): same Tags column as the Active tab.
        id: "tags",
        enableSorting: false,
        header: "Tags",
        cell: ({ row }) => {
          const tags = Array.isArray(row.original.customerTag)
            ? row.original.customerTag.join(", ")
            : String(row.original.customerTag || "");
          return (
            <div
              title={tags}
              className={
                "w-[110px] truncate text-sm font-semibold normal-case text-gray-800"
              }>
              {tags}
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm normal-case text-gray-400",
          className: "px-4 py-2",
        },
      },
    ],
    [
      allSelected,
      handleSelectAll,
      handleToggleCustomer,
      navigate,
      selectedCustomers,
      openCompose,
    ],
  );

  if ((isLoadingContacts && !contacts) || isDeleting) {
    return <LottieLoading />;
  }

  return (
    <FormSection name={""} showHeading={false}>
      {/* Phones (QA batch 11): the search box fits the screen width. */}
      <div className={"flex justify-between max-md:!flex-wrap max-md:gap-3"}>
        <div className={"flex flex-col max-md:w-full max-md:min-w-0"}>
          <div className={"text-lg font-bold"}>
            {isLoadError
              ? "Contacts"
              : `${contactsCountLabel(contacts?.[0]?.totalRecords || 0)}`}
          </div>

          <div className={"flex gap-2 max-md:w-full max-md:min-w-0"}>
            <div
              className={
                "input input-bordered flex items-center gap-2 max-md:w-full max-md:min-w-0"
              }>
              <IoSearch size={"20px"} className={"shrink-0"} />
              <input
                className={
                  "w-[250px] outline-none max-md:w-0 max-md:min-w-0 max-md:flex-1"
                }
                placeholder={"Search contacts"}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
              />
              <button
                type={"button"}
                aria-label={"Clear search"}
                className={`flex h-5 w-5 flex-none cursor-pointer items-center justify-center text-lg leading-none text-gray-400 hover:text-gray-600 ${
                  search ? "" : "invisible"
                }`}
                onClick={() => setSearch("")}>
                ×
              </button>
            </div>
          </div>
        </div>

        {hasSelectedCustomers && (
          <div className={"flex items-center gap-4"}>
            <span
              className={
                "mr-1 whitespace-nowrap text-[13px] font-bold text-[#6B4EFF]"
              }>
              {selectedCustomersLabel}
            </span>

            <Button
              type={"button"}
              variant={"outline"}
              size={"md"}
              width={100}
              leftIcon={<LuTrash2 size={16} strokeWidth={1.9} />}
              onClick={() => setIsOpenDelete(true)}>
              Delete
            </Button>

            <Button
              type={"button"}
              variant={"ghost"}
              size={"md"}
              iconOnly
              title={"Clear selection"}
              className={"text-[#6B7185] hover:bg-[#F1F2F5]"}
              leftIcon={<LuX size={16} strokeWidth={2.2} />}
              onClick={() => setSelectedCustomers([])}
            />
          </div>
        )}
      </div>

      {isLoadError && (
        <div
          role={"alert"}
          className={
            "my-3 flex flex-wrap items-center gap-3 rounded-[10px] border border-[#F6D2D2] bg-[#FFF5F5] px-3.5 py-2.5 text-[13px] font-medium text-[#B42318]"
          }>
          <span>
            The archived contacts couldn&apos;t be loaded. Please try again.
          </span>
          <button
            type={"button"}
            onClick={() => refetch()}
            className={"font-bold text-[#6B4EFF] hover:underline"}>
            Try again
          </button>
        </div>
      )}

      <Table
        columns={contactColumns}
        data={contacts || []}
        getRowId={(contact) => contact.customerId || contact.id || ""}
        tableClassName={"w-full"}
        theadClassName={"border-b-[1px] border-gray-300"}
        rowClassName={"border-b-[1px] border-gray-300 hover:bg-gray-100"}
      />

      {isOpenDelete && (
        <ContactDeleteConfirmModal
          onClose={() => setIsOpenDelete(false)}
          onConfirm={handleDeleteContacts}
          contactName={selectedContactName}
          title={deleteModalTitle}
          description={deleteModalDescription}
          isLoading={isDeleting}
        />
      )}

      {!!contacts?.length && (
        <ListPager
          page={page}
          pageSize={20}
          total={contacts?.[0]?.totalRecords || 0}
          disabled={isLoadingContacts}
          onChange={setPageParam}
        />
      )}
      {composeModal}
    </FormSection>
  );
};
